import { useAmountFormatter } from '../../diagram/formatting';
import { ep } from '../../diagram/numbers';
import { SankeyFlow } from '../../diagram/types';
import { useSankeyFlowInteraction } from '../SankeyInteractionProvider/utils';
import FlowEditPopover from './popovers/FlowEditPopover';
import { buildFlowPath } from './utils';

type Props = {
  flow: SankeyFlow;
};

const SankeyFlowPath: React.FC<Props> = ({ flow }) => {
  const { handleMouseOut, handleMouseOver } = useSankeyFlowInteraction(flow);

  const { isFlat, d } = buildFlowPath(flow);

  // ALWAYS take the target node's color for the flow color
  const strokeColor = flow.target.color;

  // Set up alternative values to enable the current flow to be
  // rendered as either flat or curved:
  // When a flow is FLAT:
  //  * It's really a parallelogram, so it needs a 'fill' value.
  //  * We still add a stroke because very angled flows can look too
  //    thin otherwise. (They still can, even with the stroke.)
  // When a flow is CURVED:
  //  * No fill; only stroke-width!
  //  * stroke-width is set to at least 1px so tiny flows can be seen.
  const strokeWidth = ep(isFlat ? 0.5 : Math.max(1, flow.dy));
  const fillColor = isFlat ? strokeColor : 'none';

  // Use 45% of node's opacity for regular flow colors and 30% of that value
  // for back flows that are a part of a cycle. We still want to show those but
  // we want to make them much less prominent until we start rendering them well.
  const opacity = 0.45 * (flow.isBackFlow ? 0.3 : 1);

  const formatAmount = useAmountFormatter();

  return (
    <FlowEditPopover flow={flow}>
      <path
        cursor="pointer"
        d={d}
        fill={fillColor}
        opacity={opacity}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
      >
        <title>
          {flow.source.name} → {flow.target.name}:{' '}
          {formatAmount(Number(flow.amount))}
        </title>
      </path>
    </FlowEditPopover>
  );
};

export default SankeyFlowPath;
